import React from "react";
import styled from "styled-components";

import { colors } from "utils";
import { ProficiencyLevelBadge } from "components/pages/Educator/Proficiency/ProficiencyLevelBadge";
import {
  KEY_LEVELS,
  distributionSegments,
} from "./proficiencyStandards";

// The distribution block (final frame 4422:123602): the title "Proficiency by
// Standard", the three-badge Key,
// and one stacked bar whose segments are sized by their share of the standards
// the table is showing.
//
// The badge is the SAME component the Class Insights rubric draws, at the 48px
// the design asks for. A second drawing of the same badge is a second thing to
// keep in step with the rubric it explains.

export const ProficiencyDistribution = ({ distribution }) => {
  const segments = distributionSegments(distribution);
  // The empty-bar sentence is only true with no firm level AND no
  // work-in-progress standard. The firm-level half is the ternary below, which
  // only reads this value once `segments` is already empty.
  const hasWorkInProgress = Number(distribution?.in_progress) > 0;

  return (
    <Section data-testid="proficiency-distribution">
      <Title>Proficiency by Standard</Title>

      <Key>
        {KEY_LEVELS.map((entry) => (
          <KeyItem key={entry.level}>
            <ProficiencyLevelBadge
              level={entry.level}
              color={entry.color}
              size={48}
              fontSize={18}
            />
            <KeyLabel>{entry.label}</KeyLabel>
          </KeyItem>
        ))}
      </Key>

      <BarBlock>
        {segments.length > 0 ? (
          <Bar
            role="img"
            aria-label={segments
              .map((s) => `Level ${s.level}: ${s.label}`)
              .join(", ")}
          >
            {segments.map((segment, index) => (
              <Segment
                key={segment.level}
                data-testid={`distribution-segment-${segment.level}`}
                data-count={segment.count}
                $fill={segment.fill}
                $width={segment.widthPct}
                $first={index === 0}
                $last={index === segments.length - 1}
              >
                <Count $chip={segment.chip}>{segment.label}</Count>
              </Segment>
            ))}
          </Bar>
        ) : (
          // An empty bar would read as a measurement of zero. Nothing has been
          // measured to a firm level yet, which is a different statement. With
          // work in progress neither is true, so nothing is drawn.
          !hasWorkInProgress && (
            <EmptyBar data-testid="distribution-empty">
              No standard has enough evidence for a proficiency level yet.
            </EmptyBar>
          )
        )}
      </BarBlock>
    </Section>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Title = styled.h2`
  margin: 0;
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0;
  text-align: center;
  color: ${colors.nightlyWoods500};
`;

const Key = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: center;
`;

const KeyItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// The design breaks each label after its first word ("Ready / to Lead"), which
// `white-space: pre-line` renders from the newline the label already carries.
const KeyLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  white-space: pre-line;
`;

const BarBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 32px;
  width: 100%;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  height: 72px;
  width: 100%;
`;

const Segment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  height: 100%;
  min-width: 0;
  width: ${(props) => props.$width}%;
  background: ${(props) => props.$fill};
  border-top-left-radius: ${(props) => (props.$first ? "16px" : "0")};
  border-bottom-left-radius: ${(props) => (props.$first ? "16px" : "0")};
  border-top-right-radius: ${(props) => (props.$last ? "16px" : "0")};
  border-bottom-right-radius: ${(props) => (props.$last ? "16px" : "0")};
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  background: ${(props) => props.$chip};
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const EmptyBar = styled.p`
  margin: 0;
  padding: 24px 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.milkGlass950};
  text-align: center;
`;
